import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useAppData } from '@/context/AppDataContext';
import { formatCurrencySpaced, maskCurrency, getCurrencySymbol } from '@/lib/market';
import {
  Search,
  Download,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Eye,
  EyeOff,
  Calendar,
  Filter,
  FileText,
  Edit2,
  Trash2,
  X,
  Save
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Direction icon map (stays in source)
const TXN_DIRECTION_ICONS = { in: ArrowDownRight, out: ArrowUpRight };

const emptyTransaction = {
  portfolio_id: '',
  asset_class: 'STOCKS',
  transaction_type: 'BUY',
  symbol: '',
  name: '',
  date: new Date().toISOString().split('T')[0],
  quantity: '',
  price: '',
  amount: '',
  charges: '0',
  stt: '0',
  gst: '0',
  stamp_duty: '0',
  other_charges: '0',
  notes: '',
  broker: '',
};

export default function TransactionsPage() {
  const { user } = useAuth();
  const { transactionTypeMap, assetClasses } = useAppData();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedAssetClass, setSelectedAssetClass] = useState('all');
  const [showValues, setShowValues] = useState(true);
  
  // Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState(emptyTransaction);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedType !== 'all') params.transaction_type = selectedType;
      if (selectedAssetClass !== 'all') params.asset_class = selectedAssetClass;
      
      const [txnResponse, portfoliosResponse] = await Promise.all([
        axios.get(`${API}/markets/transactions`, { params }),
        axios.get(`${API}/markets/portfolios`)
      ]);
      
      setTransactions(txnResponse.data);
      setPortfolios(portfoliosResponse.data || []);
      
      // Set default portfolio for new transactions
      if (portfoliosResponse.data?.length > 0 && !currentTransaction.portfolio_id) {
        setCurrentTransaction(prev => ({ ...prev, portfolio_id: portfoliosResponse.data[0].id }));
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [selectedType, selectedAssetClass]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (value) => {
    if (!showValues) return maskCurrency(user?.market);
    return formatCurrencySpaced(value, user?.market);
  };
  const currSym = getCurrencySymbol(user?.market);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Filter transactions
  const filteredTransactions = transactions.filter(t => {
    const matchesSearch = 
      t.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // Calculate totals
  const totalBuy = filteredTransactions
    .filter(t => ['BUY', 'SIP'].includes(t.transaction_type))
    .reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalSell = filteredTransactions
    .filter(t => ['SELL', 'REDEEM'].includes(t.transaction_type))
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  const handleAddNew = () => {
    setCurrentTransaction({
      ...emptyTransaction,
      portfolio_id: portfolios[0]?.id || ''
    });
    setIsEditing(false);
    setIsDialogOpen(true);
  };

  const handleEdit = (txn) => {
    setCurrentTransaction({
      ...txn,
      quantity: txn.quantity || '',
      price: txn.price || '',
      amount: txn.amount || '',
      charges: txn.charges || '0',
      stt: txn.stt || '0',
      gst: txn.gst || '0',
      stamp_duty: txn.stamp_duty || '0',
      other_charges: txn.other_charges || '0',
      notes: txn.notes || '',
      broker: txn.broker || '',
    });
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const handleDelete = async (txnId) => {
    try {
      await axios.delete(`${API}/public-markets/transactions/${txnId}`);
      toast.success('Transaction deleted successfully');
      setDeleteConfirmId(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      toast.error('Failed to delete transaction');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Calculate net amount
      const amount = parseFloat(currentTransaction.amount) || 0;
      const charges = parseFloat(currentTransaction.charges) || 0;
      const stt = parseFloat(currentTransaction.stt) || 0;
      const gst = parseFloat(currentTransaction.gst) || 0;
      const stampDuty = parseFloat(currentTransaction.stamp_duty) || 0;
      const otherCharges = parseFloat(currentTransaction.other_charges) || 0;
      
      const totalCharges = charges + stt + gst + stampDuty + otherCharges;
      const isBuy = ['BUY', 'SIP'].includes(currentTransaction.transaction_type);
      const netAmount = isBuy ? amount + totalCharges : amount - totalCharges;

      const data = {
        portfolio_id: currentTransaction.portfolio_id,
        asset_class: currentTransaction.asset_class,
        transaction_type: currentTransaction.transaction_type,
        symbol: currentTransaction.symbol,
        name: currentTransaction.name,
        date: currentTransaction.date,
        quantity: currentTransaction.quantity ? parseFloat(currentTransaction.quantity) : null,
        price: currentTransaction.price ? parseFloat(currentTransaction.price) : null,
        amount: amount,
        charges: charges,
        stt: stt,
        gst: gst,
        stamp_duty: stampDuty,
        other_charges: otherCharges,
        net_amount: netAmount,
        notes: currentTransaction.notes,
        broker: currentTransaction.broker,
      };

      if (isEditing) {
        await axios.put(`${API}/public-markets/transactions/${currentTransaction.id}`, data);
        toast.success('Transaction updated successfully');
      } else {
        await axios.post(`${API}/public-markets/transactions`, data);
        toast.success('Transaction added successfully');
      }

      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving transaction:', error);
      toast.error(error.response?.data?.detail || 'Failed to save transaction');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setCurrentTransaction(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-calculate amount if quantity and price are provided
      if ((field === 'quantity' || field === 'price') && updated.quantity && updated.price) {
        const qty = parseFloat(updated.quantity) || 0;
        const price = parseFloat(updated.price) || 0;
        updated.amount = (qty * price).toString();
      }
      
      return updated;
    });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-[#ffd87a] animate-spin mx-auto mb-4" />
          <p className="text-[#4a4a4a]">Loading transactions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="transactions-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#dae4c4] flex items-center justify-center shadow-sm">
            <FileText className="w-5 h-5 text-[#1a1a1a]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#1a1a1a]">Transactions</h1>
            <p className="text-sm text-[#4a4a4a]">{filteredTransactions.length} transactions</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowValues(!showValues)}
            className="border-[#e5e5e5] text-[#4a4a4a]"
          >
            {showValues ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            className="border-[#e5e5e5] text-[#4a4a4a]"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            onClick={handleAddNew}
            className="bg-[#ffd87a] hover:bg-[#f5cc6a] text-black"
            data-testid="add-transaction-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Transaction
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-white border-[#e5e5e5]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">Total Buy / SIP</p>
                  <p className="text-2xl font-bold text-[#1a1a1a]">{formatCurrency(totalBuy)}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-[#ffd87a]/10 flex items-center justify-center">
                  <ArrowDownRight className="w-5 h-5 text-[#ffd87a]" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#e5e5e5]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">Total Sell / Redeem</p>
                  <p className="text-2xl font-bold text-[#1a1a1a]">{formatCurrency(totalSell)}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-[#ffd87a]/10 flex items-center justify-center">
                  <ArrowUpRight className="w-5 h-5 text-[#ffd87a]" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#e5e5e5]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">Net Flow</p>
                  <p className={`text-2xl font-bold ${totalBuy - totalSell >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>
                    {formatCurrency(totalBuy - totalSell)}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-[#ffd87a]/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#ffd87a]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#4a4a4a]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by symbol or name..."
              className="pl-10 bg-white border-[#e5e5e5] text-[#1a1a1a]"
            />
          </div>
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger className="w-[150px] bg-white border-[#e5e5e5]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {Object.values(transactionTypeMap).map(row => (
                <SelectItem key={row.key} value={row.key}>{row.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedAssetClass} onValueChange={setSelectedAssetClass}>
            <SelectTrigger className="w-[150px] bg-white border-[#e5e5e5]">
              <SelectValue placeholder="Asset Class" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Assets</SelectItem>
              {assetClasses.map(ac => (
                <SelectItem key={ac.key} value={ac.key.toUpperCase()}>{ac.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Transactions List */}
        <Card className="bg-white border-[#e5e5e5]">
          <CardContent className="p-0">
            {filteredTransactions.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-[#4a4a4a] mx-auto mb-4" />
                <p className="text-[#4a4a4a] mb-4">No transactions found</p>
                <Button onClick={handleAddNew} className="bg-[#ffd87a] hover:bg-[#f5cc6a] text-black">
                  <Plus className="w-4 h-4 mr-2" /> Add Your First Transaction
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-[#e5e5e5]">
                {filteredTransactions.map((txn) => {
                  const txnRow = transactionTypeMap[txn.transaction_type] || transactionTypeMap['BUY'] || {};
                  const typeConfig = { label: txnRow.label || txn.transaction_type, color: txnRow.data?.color || 'bg-[#ffd87a]/20 text-[#1a1a1a]' };
                  const TypeIcon = TXN_DIRECTION_ICONS[txnRow.data?.direction] || ArrowDownRight;

                  return (
                    <div
                      key={txn.id}
                      className="flex items-center justify-between p-4 hover:bg-[#f9f9f9] transition-colors"
                      data-testid={`transaction-${txn.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg ${typeConfig.color.split(' ')[0]} flex items-center justify-center`}>
                          <TypeIcon className={`w-5 h-5 ${typeConfig.color.split(' ')[1]}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-[#1a1a1a]">{txn.symbol}</p>
                            <Badge variant="outline" className={typeConfig.color}>
                              {typeConfig.label}
                            </Badge>
                          </div>
                          <p className="text-sm text-[#4a4a4a]">{txn.name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-sm text-[#4a4a4a]">{formatDate(txn.date)}</p>
                          <p className="text-xs text-[#4a4a4a]">
                            {txn.quantity ? `${txn.quantity} @ ${currSym}${txn.price?.toLocaleString('en-IN')}` : '-'}
                          </p>
                        </div>
                        <div className="text-right min-w-[100px]">
                          <p className="font-semibold text-[#1a1a1a]">{formatCurrency(txn.amount)}</p>
                          <p className="text-xs text-[#4a4a4a]">{txn.asset_class}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(txn)}
                            className="text-[#4a4a4a] hover:text-[#2a2a2a]"
                            data-testid={`edit-${txn.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirmId(txn.id)}
                            className="text-[#4a4a4a] hover:text-[#c97a3a]"
                            data-testid={`delete-${txn.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Transaction Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white border-[#e5e5e5]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1a1a]">
              {isEditing ? 'Edit Transaction' : 'Add New Transaction'}
            </DialogTitle>
            <DialogDescription>
              Fill in the transaction details below
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label htmlFor="portfolio_id">Portfolio *</Label>
              <Select value={currentTransaction.portfolio_id} onValueChange={(v) => updateField('portfolio_id', v)}>
                <SelectTrigger className="bg-white border-[#e5e5e5]">
                  <SelectValue placeholder="Select portfolio" />
                </SelectTrigger>
                <SelectContent>
                  {portfolios.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="asset_class">Asset Class *</Label>
              <Select value={currentTransaction.asset_class} onValueChange={(v) => updateField('asset_class', v)}>
                <SelectTrigger className="bg-white border-[#e5e5e5]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {assetClasses.map(ac => (
                    <SelectItem key={ac.key} value={ac.key.toUpperCase()}>{ac.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="transaction_type">Transaction Type *</Label>
              <Select value={currentTransaction.transaction_type} onValueChange={(v) => updateField('transaction_type', v)}>
                <SelectTrigger className="bg-white border-[#e5e5e5]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(transactionTypeMap).map(row => (
                    <SelectItem key={row.key} value={row.key}>{row.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="date">Date *</Label>
              <Input
                id="date"
                type="date"
                value={currentTransaction.date}
                onChange={(e) => updateField('date', e.target.value)}
                className="bg-white border-[#e5e5e5]"
              />
            </div>
            <div>
              <Label htmlFor="symbol">Symbol *</Label>
              <Input
                id="symbol"
                value={currentTransaction.symbol}
                onChange={(e) => updateField('symbol', e.target.value.toUpperCase())}
                placeholder="RELIANCE, HDFC, etc."
                className="bg-white border-[#e5e5e5]"
              />
            </div>
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={currentTransaction.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="Reliance Industries Ltd"
                className="bg-white border-[#e5e5e5]"
              />
            </div>
            <div>
              <Label htmlFor="quantity">Quantity</Label>
              <Input
                id="quantity"
                type="number"
                value={currentTransaction.quantity}
                onChange={(e) => updateField('quantity', e.target.value)}
                placeholder="100"
                className="bg-white border-[#e5e5e5]"
              />
            </div>
            <div>
              <Label htmlFor="price">Price ({currSym})</Label>
              <Input
                id="price"
                type="number"
                value={currentTransaction.price}
                onChange={(e) => updateField('price', e.target.value)}
                placeholder="2500"
                className="bg-white border-[#e5e5e5]"
              />
            </div>
            <div>
              <Label htmlFor="amount">Amount ({currSym}) *</Label>
              <Input
                id="amount"
                type="number"
                value={currentTransaction.amount}
                onChange={(e) => updateField('amount', e.target.value)}
                placeholder="250000"
                className="bg-white border-[#e5e5e5]"
              />
            </div>
            <div>
              <Label htmlFor="broker">Broker</Label>
              <Input
                id="broker"
                value={currentTransaction.broker}
                onChange={(e) => updateField('broker', e.target.value)}
                placeholder="Zerodha, Groww, etc."
                className="bg-white border-[#e5e5e5]"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-sm text-[#4a4a4a] mb-2 block">Charges & Taxes</Label>
              <div className="grid grid-cols-5 gap-2">
                <div>
                  <Label htmlFor="charges" className="text-xs">Brokerage</Label>
                  <Input
                    id="charges"
                    type="number"
                    value={currentTransaction.charges}
                    onChange={(e) => updateField('charges', e.target.value)}
                    className="bg-white border-[#e5e5e5]"
                  />
                </div>
                <div>
                  <Label htmlFor="stt" className="text-xs">STT</Label>
                  <Input
                    id="stt"
                    type="number"
                    value={currentTransaction.stt}
                    onChange={(e) => updateField('stt', e.target.value)}
                    className="bg-white border-[#e5e5e5]"
                  />
                </div>
                <div>
                  <Label htmlFor="gst" className="text-xs">GST</Label>
                  <Input
                    id="gst"
                    type="number"
                    value={currentTransaction.gst}
                    onChange={(e) => updateField('gst', e.target.value)}
                    className="bg-white border-[#e5e5e5]"
                  />
                </div>
                <div>
                  <Label htmlFor="stamp_duty" className="text-xs">Stamp</Label>
                  <Input
                    id="stamp_duty"
                    type="number"
                    value={currentTransaction.stamp_duty}
                    onChange={(e) => updateField('stamp_duty', e.target.value)}
                    className="bg-white border-[#e5e5e5]"
                  />
                </div>
                <div>
                  <Label htmlFor="other_charges" className="text-xs">Other</Label>
                  <Input
                    id="other_charges"
                    type="number"
                    value={currentTransaction.other_charges}
                    onChange={(e) => updateField('other_charges', e.target.value)}
                    className="bg-white border-[#e5e5e5]"
                  />
                </div>
              </div>
            </div>
            <div className="col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={currentTransaction.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                placeholder="Additional notes"
                className="bg-white border-[#e5e5e5]"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="border-[#e5e5e5]">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#ffd87a] hover:bg-[#f5cc6a] text-black">
              {saving ? 'Saving...' : (isEditing ? 'Update Transaction' : 'Add Transaction')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent className="bg-white border-[#e5e5e5]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1a1a]">Delete Transaction</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this transaction? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)} className="border-[#e5e5e5]">
              Cancel
            </Button>
            <Button onClick={() => handleDelete(deleteConfirmId)} className="bg-[#c97a3a] hover:bg-[#b06530]">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
